import { ChatMessageAttachmentSchema } from "@albertine/types/openapi/concierge-v1";
import { addMilliseconds } from "date-fns/addMilliseconds";
import { compareAsc } from "date-fns/compareAsc";
import { max } from "date-fns/max";
import { parseISO } from "date-fns/parseISO";
import { useMemo, useSyncExternalStore } from "react";
import { z } from "zod";

const STORAGE_KEY = "albertine-mini-apps-chat-outbox";

const confirmedBookingSchema = z.object({
    id: z.string().min(1),
    title: z.string(),
    imageUrl: z.string().optional(),
    options: z.array(z.object({ id: z.string(), title: z.string() })).optional(),
});

const chatMessageReplySchema = z.object({
    id: z.string().min(1),
    title: z.string(),
    message: z.string(),
    imageUrl: z.string().optional(),
});

const chatOutboxUploadSchema = z.object({
    localId: z.string().min(1),
    fileName: z.string().min(1),
    mimeType: z.string().min(1),
    fileUploadId: z.string().min(1).optional(),
});

const chatOutboxDraftSchema = z.object({
    sendingId: z.string().min(1),
    scopeKey: z.string().min(1),
    message: z.string().default(""),
    messageHtml: z.string(),
    attachments: z.array(ChatMessageAttachmentSchema),
    uploads: z.array(chatOutboxUploadSchema).default([]),
    confirmedBookings: z.array(confirmedBookingSchema).optional(),
    reply: chatMessageReplySchema.optional(),
});

const chatOutboxEntrySchema = chatOutboxDraftSchema.extend({
    createdAt: z.iso.datetime(),
    status: z.enum(["sending", "sent", "failed"]),
});

const chatOutboxSchema = z.array(chatOutboxEntrySchema);

export type ChatOutboxUpload = z.infer<typeof chatOutboxUploadSchema>;
export type ChatOutboxDraft = z.infer<typeof chatOutboxDraftSchema>;
export type ChatOutboxEntry = z.infer<typeof chatOutboxEntrySchema>;

const EMPTY_ENTRIES: readonly ChatOutboxEntry[] = [];

const listeners = new Set<() => void>();

let entries: readonly ChatOutboxEntry[] = EMPTY_ENTRIES;

export function getChatOutboxEntries(): readonly ChatOutboxEntry[] {
    return entries;
}

export function restoreChatOutbox(): void {
    const restored = markSendingAsFailed(dropUnsendableEntries(readStoredEntries()));
    entries = restored;
    writeStoredEntries(restored);
    notify();
}

export function enqueueChatOutboxEntry(
    draft: ChatOutboxDraft,
    options: { afterSentAt?: string } = {},
): void {
    const existing = entries.find((entry) => entry.sendingId === draft.sendingId);

    setEntries([
        ...entries.filter((entry) => entry.sendingId !== draft.sendingId),
        {
            ...draft,
            createdAt:
                existing?.createdAt ??
                createOutboxOrderTimestamp(draft.scopeKey, options.afterSentAt),
            status: "sending",
        },
    ]);
}

export function recordChatOutboxUploadId(
    sendingId: string,
    localId: string,
    fileUploadId: string,
): void {
    setEntries(
        entries.map((entry) =>
            entry.sendingId === sendingId
                ? {
                      ...entry,
                      uploads: entry.uploads.map((upload) =>
                          upload.localId === localId
                              ? { ...upload, fileUploadId }
                              : upload,
                      ),
                  }
                : entry,
        ),
    );
}

export function markChatOutboxEntryFailed(sendingId: string): void {
    const entry = entries.find((candidate) => candidate.sendingId === sendingId);
    if (!entry || entry.status === "failed" || entry.status === "sent") {
        return;
    }

    replaceEntry({ ...entry, status: "failed" });
}

export function markChatOutboxEntrySent(sendingId: string): void {
    const entry = entries.find((candidate) => candidate.sendingId === sendingId);
    if (!entry || entry.status === "sent") {
        return;
    }

    replaceEntry({ ...entry, status: "sent" });
}

export function removeChatOutboxEntry(sendingId: string): void {
    setEntries(entries.filter((entry) => entry.sendingId !== sendingId));
}

/** Marks the exact local row represented by a confirmed API/SSE message as sent. */
export function reconcileChatOutboxWithSentMessage(message: {
    sendingId?: string | null;
    sender: { type: string };
}): void {
    if (message.sender.type !== "member" || !message.sendingId) {
        return;
    }

    if (entries.some((entry) => entry.sendingId === message.sendingId)) {
        markChatOutboxEntrySent(message.sendingId);
    }
}

export function clearChatOutbox(): void {
    setEntries([]);
}

export function subscribeToChatOutbox(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useChatOutbox(scopeKey: string): ChatOutboxEntry[] {
    const allEntries = useSyncExternalStore(
        subscribeToChatOutbox,
        getChatOutboxEntries,
        getChatOutboxEntries,
    );

    return useMemo(
        () =>
            allEntries
                .filter((entry) => entry.scopeKey === scopeKey)
                .toSorted((left, right) =>
                    compareAsc(parseISO(left.createdAt), parseISO(right.createdAt)),
                ),
        [allEntries, scopeKey],
    );
}

function setEntries(next: readonly ChatOutboxEntry[]): void {
    entries = next;
    writeStoredEntries(next);
    notify();
}

function replaceEntry(updated: ChatOutboxEntry): void {
    setEntries(
        entries.map((entry) =>
            entry.sendingId === updated.sendingId ? updated : entry,
        ),
    );
}

function createOutboxOrderTimestamp(
    scopeKey: string,
    afterSentAt: string | undefined,
): string {
    const anchors = [
        new Date(),
        ...entries
            .filter((entry) => entry.scopeKey === scopeKey)
            .map((entry) => parseISO(entry.createdAt)),
    ];

    if (afterSentAt) {
        anchors.push(parseISO(afterSentAt));
    }

    return addMilliseconds(max(anchors), 1).toISOString();
}

function markSendingAsFailed(
    stored: readonly ChatOutboxEntry[],
): readonly ChatOutboxEntry[] {
    return stored.map((entry) =>
        entry.status === "sending" ? { ...entry, status: "failed" } : entry,
    );
}

function dropUnsendableEntries(
    stored: readonly ChatOutboxEntry[],
): readonly ChatOutboxEntry[] {
    const restorable: ChatOutboxEntry[] = [];

    for (const entry of stored) {
        if (entry.status === "sent") {
            continue;
        }

        const uploads = entry.uploads.filter((upload) => upload.fileUploadId);
        const attachments = entry.attachments.filter(
            (attachment) =>
                attachment.type !== "file" || !attachment.url.startsWith("blob:"),
        );

        if (
            entry.message.trim().length === 0 &&
            attachments.length === 0 &&
            uploads.length === 0
        ) {
            continue;
        }

        restorable.push({ ...entry, attachments, uploads });
    }

    return restorable;
}

function readStoredEntries(): readonly ChatOutboxEntry[] {
    const storage = getStorage();
    if (!storage) {
        return EMPTY_ENTRIES;
    }

    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
        return EMPTY_ENTRIES;
    }

    try {
        const parsed = chatOutboxSchema.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : EMPTY_ENTRIES;
    } catch {
        return EMPTY_ENTRIES;
    }
}

function writeStoredEntries(next: readonly ChatOutboxEntry[]): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }

    if (next.length === 0) {
        storage.removeItem(STORAGE_KEY);
        return;
    }

    storage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function getStorage(): Storage | null {
    try {
        return typeof window === "undefined" ? null : window.localStorage;
    } catch {
        return null;
    }
}

function notify(): void {
    for (const listener of listeners) {
        listener();
    }
}

function handleStorageEvent(event: StorageEvent): void {
    if (event.key !== null && event.key !== STORAGE_KEY) {
        return;
    }

    entries = readStoredEntries();
    notify();
}

if (typeof window !== "undefined") {
    window.addEventListener("storage", handleStorageEvent);
    restoreChatOutbox();
}
